import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wrench, Gauge, Fuel } from "lucide-react";

const EQUIP_TYPES = ["Tracteur", "Pompe", "Véhicule", "Groupe électrogène", "Irrigation", "Équipement piscicole", "Autre"];
const EQUIP_STATUS = ["opérationnel", "maintenance", "hors_service"];

export default function Equipements() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [newE, setNewE] = useState({ name: "", type: "Tracteur", status: "opérationnel", hours: "", fuel_l: "", next_maintenance: "", notes: "" });

  async function load() {
    const r = await api.get("/equipment");
    setItems(r.data);
  }
  useEffect(() => { load(); }, []);

  async function addItem() {
    if (!newE.name) return toast.error("Nom requis");
    try {
      await api.post("/equipment", {
        ...newE, hours: parseFloat(newE.hours || 0), fuel_l: parseFloat(newE.fuel_l || 0),
        next_maintenance: newE.next_maintenance ? new Date(newE.next_maintenance).toISOString() : null,
      });
      setOpen(false); setNewE({ name: "", type: "Tracteur", status: "opérationnel", hours: "", fuel_l: "", next_maintenance: "", notes: "" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function setStatus(id, status) {
    try {
      await api.put(`/equipment/${id}`, { status });
      toast.success(t("updated")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delItem(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  const STATUS_COLOR = { "opérationnel": "default", "maintenance": "secondary", "hors_service": "destructive" };

  return (
    <div className="space-y-6" data-testid="equipements-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("logistics")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("equipment_page")}</h1>
          <p className="text-muted-foreground mt-1">{items.length} {t("equipment_page").toLowerCase()}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-equip-btn"><Plus className="w-4 h-4 mr-2" />{t("equipment_page")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("equipment_page")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="equip-name" value={newE.name} onChange={(e) => setNewE({ ...newE, name: e.target.value })} /></div>
              <div>
                <Label>{t("type")}</Label>
                <Select value={newE.type} onValueChange={(v) => setNewE({ ...newE, type: v })}>
                  <SelectTrigger data-testid="equip-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{EQUIP_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("hours")}</Label><Input type="number" value={newE.hours} onChange={(e) => setNewE({ ...newE, hours: e.target.value })} /></div>
              <div><Label>{t("fuel")} (L)</Label><Input type="number" value={newE.fuel_l} onChange={(e) => setNewE({ ...newE, fuel_l: e.target.value })} /></div>
              <div><Label>{t("next_maintenance")}</Label><Input type="date" value={newE.next_maintenance} onChange={(e) => setNewE({ ...newE, next_maintenance: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addItem} data-testid="equip-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {items.map((e) => (
          <Card key={e.id} className="card-hover border-border" data-testid={`equip-card-${e.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow">{e.type}</div>
                  <div className="text-lg font-semibold mt-1">{e.name}</div>
                </div>
                <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                  <SelectTrigger className="h-8 w-36" data-testid={`equip-status-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{EQUIP_STATUS.map((s) => <SelectItem key={s} value={s}>{t(`equip_${s}`) || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Gauge className="w-3 h-3" />{t("hours")}</div><div className="font-medium">{e.hours} h</div></div>
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Fuel className="w-3 h-3" />{t("fuel")}</div><div className="font-medium">{e.fuel_l} L</div></div>
                <div><div className="text-xs text-muted-foreground flex items-center gap-1"><Wrench className="w-3 h-3" />Maint.</div><div className="font-medium">{formatDate(e.next_maintenance, lang) || "—"}</div></div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => delItem(e.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground col-span-3 text-center py-8">{t("no_data")}</div>}
      </div>
    </div>
  );
}
